import { useAuth } from '@/contexts/AuthContext'
import { useAccount } from '@/contexts/AccountContext'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { UserRole } from '@/types/icrop'

/**
 * Hook to fetch user profile (role, account_id) from the profiles table.
 * This is the source of truth for role-based access control.
 */
export function useUserProfile() {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['user-profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null

      // Guard against a hung request (e.g. a stuck Supabase auth-token refresh
      // or Web Locks contention) — without this the profile query can never
      // settle and every gated route spins on "Loading..." forever. Failing
      // after 12s lets react-query retry and ultimately surface an error.
      const profilePromise = supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single()

      const timeout = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Profile request timed out')), 12000),
      )

      const { data, error } = await Promise.race([profilePromise, timeout])

      if (error) {
        throw new Error(`Failed to fetch user profile: ${error.message}`)
      }

      return data
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Combined hook that provides authentication and account information.
 * This is the main hook components should use for auth-related functionality.
 */
export function useSupabaseAuth() {
  const auth = useAuth()
  const { account } = useAccount()
  const { data: profile, isLoading: profileLoading } = useUserProfile()

  const role = (profile?.role as UserRole) || null

  const hasRole = (r: string): boolean => role === r

  const canAccess = (resource: string): boolean => {
    if (!role) return false
    if (role === 'super_admin' || role === 'account_admin') return true

    const accessRules: Record<string, UserRole[]> = {
      documents: ['super_admin', 'account_admin', 'manager', 'user', 'viewer'],
      organizations: ['super_admin', 'account_admin', 'manager'],
      groups: ['super_admin', 'account_admin', 'manager'],
      settings: ['super_admin', 'account_admin'],
      users: ['super_admin', 'account_admin'],
      reports: ['super_admin', 'account_admin', 'manager'],
    }

    const allowedRoles = accessRules[resource] || []
    return allowedRoles.includes(role)
  }

  return {
    // Auth state
    user: auth.user,
    session: auth.session,
    loading: auth.loading || profileLoading,
    error: auth.error,

    // Account & profile
    account,
    profile,
    role,

    // Auth actions
    signIn: auth.signIn,
    signUp: auth.signUp,
    signOut: auth.signOut,
    resetPassword: auth.resetPassword,

    // Utility functions
    hasRole,
    canAccess,

    // Error handling
    clearError: auth.clearError,

    // Computed properties
    isAuthenticated: !!auth.user,
    isSuperAdmin: role === 'super_admin',
    isAccountAdmin: role === 'account_admin',
    isAdmin: role === 'super_admin' || role === 'account_admin',
    accountName: account?.name || '',
    userEmail: auth.user?.email || '',

    // Permission helpers
    canManageUsers: () => canAccess('users'),
    canManageSettings: () => canAccess('settings'),
    canViewReports: () => canAccess('reports'),
    canManageOrganizations: () => canAccess('organizations'),
    canViewDocuments: () => canAccess('documents'),
  }
}

/**
 * Hook for components that only need authentication state (no account/profile)
 */
export function useAuthOnly() {
  const auth = useAuth()

  return {
    user: auth.user,
    session: auth.session,
    loading: auth.loading,
    error: auth.error,
    signIn: auth.signIn,
    signUp: auth.signUp,
    signOut: auth.signOut,
    resetPassword: auth.resetPassword,
    clearError: auth.clearError,
    isAuthenticated: !!auth.user,
  }
}
